import { App, Form } from "antd";
import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { UPDATE_INVOICE_DATA } from "./patientPayments.qql";

const usePatientPayments = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const { fullName, data: userData, refetchUser } = useCurrentUser();

  const defaultPageSize = 3;
  const [currentPageSize, setCurrentPageSize] = useState(defaultPageSize);

  const invoiceData = useMemo(() => {
    return userData && userData.invoiceData
      ? userData.invoiceData
        .map(invoice => ({
          date: invoice.invoice_date ?? "",
          file: invoice.invoice_file_path ?? "",
          price: invoice.amount ?? "",
          dateOfIssuing: invoice.appointment?.start_at ?? ""
        }))
      : [];
  }, [userData]);

  const handleLoadMore = () => {
    setCurrentPageSize(invoiceData.length > currentPageSize ? invoiceData.length : defaultPageSize);
  };

  const [updateInvoiceData, { loading: isFormEditLoading }] = useMutation(UPDATE_INVOICE_DATA, {
    fetchPolicy: "no-cache",
    onCompleted: () => {
      refetchUser();
      message.success(t("apollo.success.updateInvoiceData"));
      setIsEditing(false);
    },
    onError: ({ graphQLErrors }) => {
      message.error(graphQLErrors[0].message);
    }
  });

  const handleSubmit = (values: {
    invoiceName: string;
    invoiceSurname: string;
    invoiceCodFisc: string;
    address: string;
    cap: string;
    city: string;
    province: string;
    phone: { number: string };
  }) => {
    updateInvoiceData({
      variables: {
        name: values.invoiceName,
        surname: values.invoiceSurname,
        cod_fiscale: values.invoiceCodFisc,
        address: values.address,
        cap: values.cap,
        city: values.city,
        province: values.province,
        phone: values.phone.number
      }
    });
  };

  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  const billingAddress = useMemo(() => {
    if (userData && userData.invoice_name) {
      return `${userData.invoice_name} ${userData.invoice_surname}, ${userData.invoice_address} ${userData.invoice_cap} ${userData.invoice_city} ${userData.invoice_province} IT`;
    }
    return "";
  }, [userData]);

  return {
    form,
    handleSubmit,
    isEditing,
    toggleEdit,
    isFormEditLoading,
    billingAddress,
    handleLoadMore,
    fullName,
    invoiceData,
    defaultPageSize,
    currentPageSize
  };
};

export default usePatientPayments;
